// Order Devices — types for the coupon-fulfillment queue/ledger page.
// Mirrors the staff-api DeviceOrderTask GraphQL shape. Vendor by copy.

// The warranty verdict lives in ./warranty, beside the rule that produces it — the RMA row merely
// carries what the server derived.
import type { RmaWarrantyState } from './warranty';

export interface DeviceOrderCoupon {
  code: string;
  amount?: number | null;
  deviceCount?: number | null;
  used?: boolean | null;
  seq?: number | null;
  /** Which products[] line this coupon is for (0-based); undefined on legacy coupons. */
  productIndex?: number | null;
  mintedByStaffEmail?: string | null;
  mintedAt?: string | null;
}

export interface DeviceOrderTask {
  _id: string;
  invoiceId?: string | null;
  newInvoiceId?: string | null;
  stripeCustomerId?: string | null;
  customerEmail?: string | null;
  /** Account OWNER's email, resolved from stripeCustomerId server-side. Preferred for display. */
  ownerEmail?: string | null;
  accountId?: string | null;
  deviceType?: string | null;
  /** Per-device line items, e.g. ["400x OptiSigns Pro Signage Player","15x Optisigns Android…"]. */
  products?: string[] | null;
  qty?: number | null;
  amount?: number | null;
  currency?: string | null;
  /** UNPAID | PAID | PAST_DUE | VOID | VOIDED_RECREATED | DRAFT | OPEN */
  status?: string | null;
  paidAt?: string | null;
  /** HOLD | GIVE_AFTER_PAYMENT | PROVIDED | '' */
  provideGate?: string | null;
  coupons?: DeviceOrderCoupon[] | null;
  /** True ⇒ a coupon was issued before payment on management approval (the payment-gate override). */
  couponIssuedBeforePayment?: boolean | null;
  /** The deadline: approval time + the enforcement term. Past this, screens are switched off. */
  chaseDueDate?: string | null;

  // --- Stripe payment terms. `netTermsDays` scopes the chase: Net 60+ is never chased,
  // because the invoice is not even due by the deadline.
  dueDate?: string | null;
  netTermsDays?: number | null;
  collectionMethod?: string | null;

  // --- the management approval on record
  gateOverrideBy?: string | null;
  gateOverrideAt?: string | null;
  gateOverrideReason?: string | null;

  // --- enforcement trail
  warnedAt?: string | null;
  /** Non-null ⇒ this account's screens are currently switched off over this order. */
  deactivatedAt?: string | null;
  deactivatedAccountId?: string | null;
  reenableNotifiedAt?: string | null;
  /**
   * Non-null ⇒ at the deadline this order's codes were pulled instead of the account being
   * switched off, because none had been redeemed. Exposure removed, customer untouched.
   */
  couponsCancelledAt?: string | null;
  /** Why enforcement could not act (e.g. no account resolvable from the invoice). */
  enforceError?: string | null;

  closedAt?: string | null;
  closedByStaffEmail?: string | null;
  /** Set when a root staffer dismissed this PAID order from the Active queue (coupon given by hand). */
  dismissedAt?: string | null;
  dismissedBy?: string | null;
  dismissedReason?: string | null;
  note?: string | null;
  createdAt?: string | null;
  lastUpdatedDate?: string | null;
}

export interface DeviceOrderTaskPage {
  items: DeviceOrderTask[];
  total: number;
}

export interface OrderDeviceNote {
  _id: string;
  staffEmail: string;
  createdAt: string;
  text: string;
}

/** The three tabs of the Order Devices page. */
export type OrderDeviceTab = 'ACTIVE' | 'ALL' | 'UNPAID_COUPONS';

/** Coupon-used filter selection. */
export type CouponUsedFilter = 'USED' | 'UNUSED';

/** Server-side filter for the deviceOrderTasks query. */
export interface DeviceOrderTaskFilter {
  tab?: OrderDeviceTab;
  search?: string;
  status?: string;
  couponUsed?: CouponUsedFilter;
}

/** Catalog-priced value of a coupon for one device line (see DeviceOrderCouponQuote). */
export interface DeviceOrderCouponQuote {
  deviceName: string;
  sku: string;
  quantity: number;
  currency: string;
  invoiceCurrency?: string | null;
  /** 'us' | 'international' — which deviceShippings branch priced the freight. */
  region: string;
  customerCountry?: string | null;
  unitPrice: number;
  deviceAmount: number;
  shippingAmount: number;
  /** Freight per device per the catalog; absent when it isn't one repeated unit price. */
  shippingUnitPrice?: number | null;
  totalAmount: number;
  /** What the invoice billed for this line — may differ from the catalog price. */
  invoicedAmount?: number | null;
}


// ── Demo device coupons (the third tab) ───────────────────────────────────────────────────────
// A Shopify code issued for a DEMO device: no invoice, no payment, no chase. Mirrors the
// staff-api DemoCoupon GraphQL shape.

/** ACTIVE | USED | EXPIRED — derived server-side from `used` + `expiresAt`, never stored. */
export type DemoCouponStatus = 'ACTIVE' | 'USED' | 'EXPIRED';

export interface DemoCoupon {
  /** The code itself — it is the row's primary key. */
  code: string;
  shopifyDiscountId: string;
  sku: string;
  /** Canonical catalog name as it was at MINT time (a later rename must not rewrite history). */
  deviceName: string;
  quantity: number;
  /** 'us' | 'international'. Always 'us' on new rows — demo devices ship US only. */
  region: string;
  amount: number;
  currency: string;
  /**
   * Who it was issued to. A RECORD, not an eligibility rule — the Shopify code stays
   * all-customers. Null on rows written before migration 020.
   */
  customerEmail?: string | null;
  /** Why it was issued — the audit trail on a path with no payment gate and no approver. */
  reason: string;
  createdBy: string;
  createdAt: string;
  expiresAt: string;
  used: boolean;
  usedAt?: string | null;
  lastSyncedAt?: string | null;
  status: DemoCouponStatus;
}

export interface DemoCouponPage {
  items: DemoCoupon[];
  total: number;
}

/** Server-side filter for the demoCoupons query. */
export interface DemoCouponFilter {
  search?: string;
  status?: DemoCouponStatus;
}

/** What a demo coupon would be worth — device + freight, catalog-priced. Sales tax excluded. */
export interface DemoCouponQuote {
  sku: string;
  deviceName: string;
  quantity: number;
  currency: string;
  region: string;
  unitPrice: number;
  deviceAmount: number;
  shippingAmount: number;
  shippingUnitPrice?: number | null;
  totalAmount: number;
}

/** What the Demo Device Coupons card sends. No prices, no region, no expiry — see the API. */
export interface CreateDemoCouponInput {
  sku: string;
  quantity: number;
  customerEmail: string;
  reason: string;
  /** Operator override of the catalog price. Omit to use the quote. */
  amount?: number;
}


// ── Amazon device coupons (Devices Fulfillment → Amazon Coupons) ──────────────────────────────
// A $10-off Amazon claim code, PRE-GENERATED in Seller Central and allocated from a pool. Mirrors
// staff-api AmazonCoupon GraphQL shape (migration 056).
//
// The one structural difference from DemoCoupon: nothing here is minted. Amazon has no API that
// creates a promo code, so a human imports a CSV batch and this allocates from it.

/** Per COUNTRY, not per region — DE and UK hold separate listings with separate SKUs. */
export type AmazonMarketplace = 'us' | 'uk' | 'de';

/**
 * The currency a batch is denominated in. Follows the marketplace and is never chosen separately:
 * a Seller Central Money Off promotion is priced in its store's currency. The house value is TEN
 * in every store — $10 / £10 / €10 — deliberately the same NUMBER rather than the same value, so
 * £10 off in the UK and $10 off in the US are different offers that share a headline figure.
 */
export type AmazonCurrency = 'usd' | 'gbp' | 'eur';

/**
 * Derived server-side, never stored, from the CAMPAIGN's end date — the only expiry Amazon
 * enforces (requester, 2026-09-24).
 *
 * There used to be a fourth state, EXPIRED_ADVISORY, for a 30-day per-code date of our own that
 * Amazon had never heard of. It meant every surface had to explain that "expired" might not mean
 * expired. Reading the expiry off the campaign instead makes the word mean one thing.
 */
export type AmazonCouponStatus = 'ACTIVE' | 'USED' | 'EXPIRED';

/**
 * One Seller Central Money Off promotion — the EXPENSIVE half.
 *
 * A promotion takes roughly 4-5 HOURS to activate; a claim code group attached to one takes 1-2
 * MINUTES. Everything describing the offer lives here so a top-up cannot restate it — above all
 * `campaignEndsAt`, the only date Amazon enforces.
 */
export interface AmazonCouponPromotion {
  id: string;
  marketplace: AmazonMarketplace;
  /** What the promotion's Product Selection lists. The key; ASIN is not unique per market. */
  sku: string;
  asin: string;
  campaignId: string;
  campaignEndsAt: string;
  discountAmount: number;
  currency: AmazonCurrency;
  createdBy: string;
  createdAt: string;
  /** Unallocated codes across EVERY group under this promotion. */
  available: number;
  total: number;
  /** How many CSVs have been imported against it. */
  groupCount: number;
  /** False once `campaignEndsAt` has passed. */
  live: boolean;
}

/** One CSV from Manage Claim Codes, attached to a promotion. Provenance only. */
export interface AmazonCouponCodeGroup {
  id: string;
  promotionId: string;
  sourceFilename: string;
  importedBy: string;
  importedAt: string;
  available: number;
  total: number;
}

export interface AmazonCoupon {
  /** The Amazon claim code — the row's primary key. */
  code: string;
  promotionId: string;
  marketplace: AmazonMarketplace;
  sku: string;
  /** What the SKU IS — `ARD-0002` → `OptiStick`. Absent for a SKU outside the verified registry. */
  deviceName?: string | null;
  asin: string;
  /** Face value of this code, in `currency`. A fixed amount, not a percentage. */
  discountAmount: number;
  currency: AmazonCurrency;
  /** The campaign's end date, carried onto every row so the ledger can show it beside expiresAt. */
  campaignEndsAt: string;
  /** Who it was allocated to. Null while the code is still pooled. */
  customerEmail?: string | null;
  /** Why — the only audit control on a path with no payment gate and no approver. */
  reason?: string | null;
  createdBy?: string | null;
  /** ALLOCATED-at, not imported-at. */
  createdAt?: string | null;
  /**
   * ADVISORY ONLY. Amazon enforces `campaignEndsAt` and nothing else, so a code past this date
   * may still be redeemable. Never tell a customer a code is dead on the strength of this field.
   */
  expiresAt?: string | null;
  used: boolean;
  usedAt?: string | null;
  /**
   * AMAZON'S redemption figure for the PROMOTION this code belongs to, from the daily Orders
   * reconcile. Identical on every row of a promotion — Amazon does not put the claim code on an
   * order, so nothing can say which code was used.
   *
   * `undefined`/null means NEVER RECONCILED, which is not the same as 0.
   */
  promotionRedemptionCount?: number | null;
  promotionReconciledAt?: string | null;
  /** Codes allocated under that promotion — the denominator. */
  promotionAllocatedCount?: number | null;
  status: AmazonCouponStatus;
}

export interface AmazonCouponPage {
  items: AmazonCoupon[];
  total: number;
}

/** Server-side filter for the amazonCoupons ledger. Pooled rows are never returned. */
export interface AmazonCouponFilter {
  search?: string;
  status?: AmazonCouponStatus;
  marketplace?: AmazonMarketplace;
}

/** How much is left to hand out, per marketplace — a pool can run dry; a mint never could. */
export interface AmazonCouponPoolStatus {
  marketplace: AmazonMarketplace;
  sku: string;
  asin: string;
  discountAmount: number;
  currency: AmazonCurrency;
  available: number;
  total: number;
  /** Soonest-ending live campaign for this marketplace, or null when none is live. */
  campaignEndsAt?: string | null;
}

/** What the allocate card sends. No code, no expiry, no operator — all server-side. */
export interface AllocateAmazonCouponInput {
  marketplace: AmazonMarketplace;
  customerEmail: string;
  reason: string;
}

/** Register a promotion — the rare, 4-5 hour path. */
export interface RegisterAmazonCouponPromotionInput {
  marketplace: AmazonMarketplace;
  sku: string;
  asin: string;
  campaignId: string;
  campaignEndsAt: string;
  /** Defaults to 10 in the marketplace's own currency. Currency is derived server-side. */
  discountAmount?: number;
}

/**
 * Attach a code group to an EXISTING promotion — the routine, 1-2 minute path.
 *
 * No SKU, no end date, no amount: all inherited through `promotionId`, so a second CSV cannot
 * contradict the first about when the codes stop working.
 */
export interface ImportAmazonCouponCodesInput {
  promotionId: string;
  sourceFilename: string;
  codes: string[];
}

// ── RMA requests (Devices Fulfillment → RMA Requests) ─────────────────────────────────────────
// One RMA a customer submitted through a HubSpot RMA form. Mirrors the staff-api
// DeviceRmaRequest GraphQL shape (public.device_rma_requests, migration 021).
//
// A PROJECTION of the HubSpot ticket plus two staff-app-owned fields. Everything except
// `verifiedAt` / `verifiedBy` is a copy the next poll may refresh, so nothing here is
// edited-in-staff-app state — there is no create or edit input in this feature at all.

/**
 * DERIVED, because HubSpot has no product/model/SKU property on the ticket: from the SOURCE FORM
 * where it is known (the customer picks the form and cannot edit it), falling back to parsing the
 * subject (which they can). UNKNOWN is a real, expected value meaning "neither signal was
 * readable — open the ticket", never "probably a stick".
 */
export type RmaDeviceModel = 'OPTISTICK' | 'PRO' | 'PROMAX' | 'UNKNOWN';

/**
 * The four HubSpot RMA forms (portal 20629229), keyed by form GUID (lower-case; GUIDs are hex).
 *
 * THE WEB-SIDE COPY. staff-api carries its own model-only twin, `FORM_MODEL` in
 * apps/api/src/app/order-devices/rma.service.ts, because apps/api deliberately does not depend on
 * this package (see apps/api/src/app/opticlock/opticlock.format.ts for the same arrangement).
 * BOTH copies are asserted against the same GUID→model table in their own tests — that shared
 * table is the drift detector, and it is what stops the badge claiming one model while the ingest
 * stored another.
 *
 * `model` is why this map is authoritative at all: a customer opens the ProMax form to return a
 * ProMax, and cannot edit that choice afterwards — unlike the ticket subject, which is a text
 * input. The sandbox/qa form is a clone of the prod OptiStick form (its own HubSpot metadata says
 * `clonedFromForm: e01be878-…`), so it means the same device.
 *
 * Which of these an environment actually ingests is a separate, per-env allowlist
 * (`RMA_INGEST_FORM_IDS`) — this map only says what each form MEANS.
 */
export const RMA_FORMS: Readonly<
  Record<string, { readonly model: RmaDeviceModel; readonly label: string }>
> = {
  'e01be878-bd49-4651-a0c6-bdde73f91d26': { model: 'OPTISTICK', label: 'OptiStick RMA form' },
  '713a2b0b-6e95-42a1-9df9-b17ecf2cec4f': { model: 'PRO', label: 'Pro Player RMA form' },
  '5842d855-58dd-493d-bfdf-46dd5b3169ae': { model: 'PROMAX', label: 'ProMax Player RMA form' },
  '9f7da923-6065-467f-9025-5091605b6648': {
    model: 'OPTISTICK',
    label: 'sandbox/qa RMA form (an OptiStick form clone)',
  },
  '2068149e-307f-4cc9-b3e1-9cf0e98cdf43': {
    model: 'OPTISTICK',
    label: 'sandbox/qa RMA form #2 (an OptiStick form clone)',
  },
  // In the HubSpot SANDBOX portal 51124796, not the live one. Ticket-scoped and marker-carrying
  // like the prod forms; only `rma_order_location` is absent, so orderLocation reads blank there.
  // NOT ALLOWLISTED anywhere. The public embed endpoint answers 200 for this guid under portal
  // 51124796, but the AUTHENTICATED forms API answers 404 NO_FORM — "no form with guid … exists on
  // this portal" — and that one is authoritative. The embed endpoint evidently does not verify the
  // portal in its path, which makes it useless for deciding where a form lives. Kept only so the
  // guid resolves to a model if it ever turns up.
  '1b192ab0-7506-469f-9f9a-dcdb9b5be278': {
    model: 'OPTISTICK',
    label: 'HubSpot-sandbox RMA form (not present on portal 51124796)',
  },
  // The parent 1b192ab0 was cloned from. Same shape, same model, also usable.
  'ebdab7d9-c2dc-4548-b1fc-b91600e2b81e': {
    model: 'OPTISTICK',
    label: 'HubSpot-sandbox RMA form (base)',
  },
  // The working Pro and ProMax forms in the same sandbox portal, replacing two earlier ids that
  // were one-field contact stubs and could never carry an RMA.
  //
  // Modelled from their SUBJECT DEFAULTS — "RMA Request for Pro Player" / "…ProMax Player" — which
  // is also what the requester said they are. Deliberately NOT from `clonedFrom`: both were cloned
  // from the sandbox ANDROID form, so lineage would map both to OPTISTICK and file every one of
  // their RMAs as a $50 device instead of a $110 or $220 one. Lineage has now disagreed with the
  // truth on four of these forms; it is not a signal worth reading.
  'fbe37158-19f6-466e-895b-da8e2bcc653a': { model: 'PRO', label: 'HubSpot-sandbox Pro Player RMA form' },
  '627a85b6-083c-4aa4-9023-cab8d0250095': { model: 'PROMAX', label: 'HubSpot-sandbox ProMax Player RMA form' },
} as const;

/** The form behind a GUID, or undefined for an unmapped/unknown one. Case-insensitive. */
export function rmaFormFor(
  formId?: string | null,
): { model: RmaDeviceModel; label: string } | undefined {
  return formId ? RMA_FORMS[formId.toLowerCase()] : undefined;
}

/** The two conditions HubSpot's own `rma_device_received` enumeration distinguishes. */
export type RmaReturnCondition = 'RECEIVED_TESTING' | 'RECEIVED' | 'RECEIVED_INCOMPLETE';
export type RmaReturnStatus = 'AWAITING' | RmaReturnCondition;

export interface DeviceRmaRequest {
  /** HubSpot's form-submission id (`conversionId`) — the row's primary key. */
  submissionId: string;
  /**
   * The short readable identity — `1042`, displayed as `RMA-1042` via `rmaNo()`.
   *
   * NOT the key. `submissionId` is, because the HubSpot poll upserts on it; this rides beside it
   * for humans. Undefined only between ingest and the next numbering pass.
   */
  rmaNumber?: number | null;
  /**
   * The ticket HubSpot created for this submission, where it created one. NULL is NORMAL — a
   * submission in a HubSpot sandbox portal creates no ticket at all.
   */
  hubspotTicketId?: string | null;
  /** The ticket subject, e.g. 'RMA Request for ProMax Player'. User-editable in HubSpot. */
  subject: string;
  deviceModel: RmaDeviceModel;
  customerEmail: string;
  orderNumber?: string | null;
  /** 'Amazon' | 'Shopify' — the only two channels the forms offer. */
  orderLocation?: string | null;
  deviceCount?: number | null;
  issueDescription?: string | null;
  /**
   * HubSpot SIGNED url for the uploaded receipt/invoice — the proof of purchase a verifier opens.
   * Signed urls expire; a link that no longer loads means go to the ticket, not that data is lost.
   */
  proofOfPurchaseUrl?: string | null;
  restockingAck?: boolean | null;
  /** Stripe DRAFT invoice for the restocking fee, if raised. Also the duplicate-charge guard. */
  restockingInvoiceId?: string | null;
  /** Stripe DASHBOARD link for the draft — not the customer-facing hosted url, which is null on drafts. */
  restockingInvoiceUrl?: string | null;
  /** What was actually billed, minor units, frozen at charge time. */
  restockingFeeMinor?: number | null;
  restockingCurrency?: string | null;
  restockingInvoicedAt?: string | null;
  restockingInvoicedBy?: string | null;
  shipToName?: string | null;
  shipToLine1?: string | null;
  shipToLine2?: string | null;
  shipToCity?: string | null;
  /** Free text in HubSpot — live data holds 'NY', 'Alabama' and 'victoria' alike. */
  shipToState?: string | null;
  shipToZip?: string | null;
  /** Free text in HubSpot, form-defaulted to 'USA' — also seen as 'US', 'Australia'. */
  shipToCountry?: string | null;
  /**
   * The shipping address as a VERIFIER corrected it — where a replacement actually goes.
   *
   * ALL-OR-NOTHING: either every required field is set or none is, so `verifiedShipToLine1` is a
   * reliable "is there a correction?" marker. Null throughout = the customer's address applies.
   * Its own fields because the seven `shipTo*` above are HubSpot's and the poll rewrites them.
   */
  verifiedShipToName?: string | null;
  verifiedShipToLine1?: string | null;
  verifiedShipToLine2?: string | null;
  verifiedShipToCity?: string | null;
  verifiedShipToState?: string | null;
  verifiedShipToZip?: string | null;
  verifiedShipToCountry?: string | null;
  verifiedShipToAt?: string | null;
  verifiedShipToBy?: string | null;
  /**
   * The HubSpot form this came from, decoded from the ticket's share-link URL. Null = unknown.
   * Also the primary `deviceModel` signal — the form cannot be edited by the customer, the
   * subject can.
   */
  sourceFormId?: string | null;
  /** The ticket's createdate — when the customer submitted the form. */
  submittedAt?: string | null;
  /** Staff-confirmed order the faulty device came from. Prefilled from `orderNumber`, editable. */
  previousOrderNumber?: string | null;
  /** What troubleshooting was done, recorded by the verifier. Staff-owned. */
  troubleshootingNotes?: string | null;
  /**
   * `YYYY-MM-DD` the device was bought — what the 13-month warranty is judged from. Staff-owned:
   * filled from the Shopify order when the number resolves, typed by hand otherwise (about half of
   * real RMAs are Amazon purchases). Null = not established, shown as such rather than guessed.
   */
  verifiedOrderDate?: string | null;
  /** `IN_WARRANTY` | `EXPIRED` | `UNKNOWN` — derived server-side on every read, never stored. */
  warrantyState?: RmaWarrantyState | null;
  warrantyEndsOn?: string | null;
  warrantyDaysOwned?: number | null;
  /** Negative once expired, so "how far past" needs no second field. */
  warrantyDaysRemaining?: number | null;
  /** The separate 30-day new-and-unused RETURN window — decides whether a refund is possible. */
  withinReturnWindow?: boolean | null;
  /**
   * How many units a VERIFIER says we replace. Its own field because `deviceCount` is HubSpot's
   * and the poll rewrites it every five minutes. Null = not corrected; the replacement order then
   * ships the customer's own figure.
   */
  verifiedDeviceCount?: number | null;
  /**
   * When a staffer confirmed ELIGIBILITY — warranty window, order number, proof of purchase,
   * first-party device. Null = nobody has checked it yet. STAFF-OWNED: the HubSpot poll never
   * writes it. NOT 'device received' or 'device tested' — HubSpot owns those.
   */
  verifiedAt?: string | null;
  verifiedBy?: string | null;
  /** Derived server-side from `verifiedAt`, never stored. */
  verified: boolean;
  /** When the faulty unit came back. Null = not yet. Staff-owned; the ingest never writes it. */
  returnedAt?: string | null;
  returnedBy?: string | null;
  returnCondition?: RmaReturnCondition | null;
  /** What was missing. Only set when returnCondition is RECEIVED_INCOMPLETE. */
  returnMissingItems?: string | null;
  /**
   * `WORKS` | `FAULTY` | `NOT_TESTED` — what testing the returned unit showed, and the basis of any
   * restocking fee (the fee is for a device returned in working order). Null = nobody has said yet;
   * `NOT_TESTED` = somebody decided not to, which is a different answer.
   */
  returnTestedOutcome?: RmaTestOutcome | null;
  /** Optional notes from testing. */
  returnTestNotes?: string | null;
  returnTestedAt?: string | null;
  returnTestedBy?: string | null;
  /**
   * AWAITING | RECEIVED | RECEIVED_INCOMPLETE — derived from the stamp. AWAITING covers both "not
   * verified yet" and "verified, nothing back"; the UI draws that distinction, because only the
   * second is a running clock.
   */
  returnStatus: RmaReturnStatus;
  /**
   * The Shopify order that shipped the replacement, `gid://shopify/Order/…`. Written once, when a
   * verifier releases it; UNIQUE server-side, so one RMA can never ship two devices.
   */
  replacementOrderId?: string | null;
  /** `#1234` — what staff quote and search Shopify by. */
  replacementOrderName?: string | null;
  /**
   * The Shopify admin page for that order. COMPUTED server-side from the store handle, never
   * stored and never assembled in the browser — link on this value or do not link.
   */
  replacementOrderUrl?: string | null;
  replacementOrderedAt?: string | null;
  replacementOrderedBy?: string | null;
  /** true = typed in by a staffer; false = staff-app created the order. Different certainty. */
  replacementOrderManual?: boolean | null;
  /** Carrier tracking for the OUTBOUND replacement — not the customer's return. */
  replacementTrackingNumber?: string | null;
  replacementTrackingUrl?: string | null;
  replacementTrackingAt?: string | null;
  replacementTrackingBy?: string | null;
  /**
   * Taken out of the working queue — and why. A dismissed request is NOT WORK: every write refuses
   * on it. Not a delete: the row is a projection of a HubSpot submission, so deleting the newest
   * one would simply be re-ingested within five minutes.
   */
  dismissedAt?: string | null;
  dismissedBy?: string | null;
  dismissedReason?: RmaDismissReason | null;
  /** The request this one duplicates — what makes a dismissal a merge rather than a hide. */
  duplicateOfRmaNumber?: number | null;
  /** Derived server-side from the stamp. */
  dismissed?: boolean | null;
  firstSeenAt: string;
  lastSyncedAt: string;
}

/** What a Shopify order-number lookup found. `found: false` is ordinary — Amazon cannot resolve. */
export interface RmaOrderLookup {
  found: boolean;
  orderName?: string | null;
  orderDate?: string | null;
  customerEmail?: string | null;
  orderUrl?: string | null;
}

/** Why a request was taken out of the queue. */
export type RmaDismissReason = 'DUPLICATE' | 'TEST' | 'WITHDRAWN' | 'NOT_AN_RMA';

/** What testing a returned unit showed. `NOT_TESTED` is a decision, not a blank. */
export type RmaTestOutcome = 'WORKS' | 'FAULTY' | 'NOT_TESTED';

export interface DeviceRmaRequestPage {
  items: DeviceRmaRequest[];
  total: number;
}

/**
 * Server-side filter for the deviceRmaRequests query. `verified` is TRI-STATE: true = verified
 * only, false = the working queue, omitted = everything.
 */
export interface DeviceRmaFilter {
  search?: string;
  deviceModel?: RmaDeviceModel;
  verified?: boolean;
}

/**
 * HOW AN RMA IS NAMED TO A HUMAN — `RMA-1042`.
 *
 * A DISPLAY PREFIX, not a stored one — the same rule as `IR-<id>` for international requests, and
 * the same reasons: storing "RMA-1042" would make the value a string to sort and search on for the
 * sake of four characters that never vary.
 *
 * Shared rather than interpolated at each site, because a queue that says "RMA-1042" beside a
 * drawer that says "1042" reads as two different records.
 */
export const RMA_PREFIX = 'RMA';

/** `1042` → `RMA-1042`. Null/undefined → '—', for the window before a number is assigned. */
export function rmaNo(n: number | string | null | undefined): string {
  return n == null || n === '' ? '\u2014' : `${RMA_PREFIX}-${n}`;
}

/**
 * Read an RMA number a person typed, in any of the four shapes it travels in.
 *
 * `RMA-1042`, `rma 1042`, `#1042` and `1042` all mean the same row — somebody pasting from a chat
 * message or a ticket subject should not have to know which shape the search box wants. Returns
 * undefined when there is no number in it at all, which is how a name search stays a name search.
 *
 * ⚠️ A BARE NUMBER IS AMBIGUOUS HERE in a way it is not for IR. This queue also holds Shopify order
 * numbers (`#28861`) and a customer's own order number, and a staffer pasting `28861` means that
 * order, not RMA-28861. The server therefore treats a bare number as a HINT — it matches the RMA
 * number as well as the text columns, never instead of them.
 */
export function parseRmaNo(input: string): number | undefined {
  const m = /^\s*(?:RMA[\s-]*)?#?\s*(\d+)\s*$/i.exec(input ?? '');
  if (!m) return undefined;
  const n = Number(m[1]);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}
